import { createContext, useContext, useMemo, useEffect, useState, forwardRef } from 'react';
import type {
  ReactNode,
  Context,
  MutableRefObject,
  Ref,
  PropsWithoutRef,
  ComponentProps
} from 'react';
import { createPortal } from 'react-dom';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
import type { WebTarget } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';

import '../../init';
import { GlobalStyle } from '../../styles';
import { ThemeMachine, BaseThemeMachine } from '../../theme';
import type { ThemeMachineLike, DefaultSettableTheme, DefaultThemeDefinition } from '../../theme';
import {
  createTranslationPack,
  DefaultTranslation,
  direction as directionOfLocale
} from '../../i18n';
import type { Translation, TranslationPack } from '../../i18n';
import { documentIsAvailable, navigatorIsAvailable, windowIsAvailable } from '../../utils';
import pkg from '../../../package.json';
import useElement from '../../hooks/useElement';
import { useConsolidatedRef } from '../../hooks';

import type PegaConnectedWatcher from './connected-watcher';

import './connected-watcher';

export interface ConfigurationProps {
  /** Any components or nodes that should be impacted by the settings applied by this Configuration component. */
  children: ReactNode;
  /** User locale as defined in [BCP-47](https://www.techonthenet.com/js/language_tags.php). */
  locale?: string;
  /** Override rendering direction of the document. Direction will be based on the locale if now provided. */
  direction?: 'ltr' | 'rtl';
  /** Object with (partial) translations. */
  translations?: Translation;
  /** Theme object used to override any or all Cosmos theme properties. */
  theme?: DefaultSettableTheme;
  /** Theme mode for the application. Can be 'light', 'dark', or 'system'. */
  themeMode?: 'light' | 'dark' | 'system';
  /** Callback function triggered when the theme mode changes. */
  onThemeModeChange?: (mode: NonNullable<ConfigurationProps['themeMode']>) => void;
  /** Disables loading Open Sans or Inter from Google Fonts. Only takes effect on the root configuration. */
  disableDefaultFontLoading?: boolean;
  /** List of style sheets to inject in style sheet target. */
  styleSheets?: string[];
  /**
   * Target element for loading styles related to `styled-components`.
   * @deprecated
   */
  styleSheetTarget?: ComponentProps<typeof StyleSheetManager>['target'];
  /**
   * Target element for rendering a `ReactDOM` portal.
   * @deprecated
   */
  portalTarget?: Parameters<typeof createPortal>[1];
  /**
   * Use native HTML5 controls for inputs instead of rich ones.
   * @default false
   */
  renderNativeControls?: boolean;
  /** String to identify this configuration instance. */
  id?: string;
  /**
   * Enable/disable testId attribute rendering for all components.
   * @default true
   */
  testIds?: boolean;
  /**
   * Ref for the configuration root.
   */
  ref?: Ref<HTMLDivElement>;
}

export interface ConfigurationContextValue {
  locale: string;
  direction: NonNullable<ConfigurationProps['direction']>;
  translations: TranslationPack;
  themeMachine: ThemeMachineLike<DefaultThemeDefinition>;
  themeMode: NonNullable<ConfigurationProps['themeMode']>;
  onThemeModeChange: ConfigurationProps['onThemeModeChange'];
  styleSheetTarget: ConfigurationProps['styleSheetTarget'];
  portalTarget: ConfigurationProps['portalTarget'];
  loadedRef: MutableRefObject<boolean>;
  renderNativeControls: boolean;
  testIds: NonNullable<ConfigurationProps['testIds']>;
  /** @internal */
  mountedStyleSheetManagers?: (typeof StyleSheetManager)[];
  /** @internal */
  initialized: boolean;
}

const defaultLocale = navigatorIsAvailable ? navigator.language : 'en';

export const ConfigurationContext = createContext<ConfigurationContextValue>({
  locale: defaultLocale,
  direction: directionOfLocale(defaultLocale),
  translations: DefaultTranslation,
  themeMachine: BaseThemeMachine,
  themeMode: 'system',
  onThemeModeChange: undefined,
  styleSheetTarget: documentIsAvailable ? document.head : undefined,
  portalTarget: documentIsAvailable ? document.body : undefined,
  loadedRef: { current: false },
  renderNativeControls: false,
  testIds: true,
  mountedStyleSheetManagers: [],
  initialized: false
});

const legacyMetaKey = Symbol.for('@pega/cosmos-react-core.metadata');

interface LegacyCosmosInstace {
  version: string;
}

declare global {
  interface Window {
    [legacyMetaKey]?: {
      push: (instance: LegacyCosmosInstace) => void;
    };
  }
}

const mountedConfigs = new Set<symbol>();

interface CosmosInstance extends LegacyCosmosInstace {
  mountedConfigs: string[];
  mountOrder: string[];
  mountCount: Record<string, number>;
}

const instance: CosmosInstance = {
  version: pkg.version,
  mountedConfigs: [],
  mountOrder: [],
  mountCount: {}
};

const mountConfig = (sym: symbol) => {
  mountedConfigs.add(sym);
  instance.mountedConfigs = [...mountedConfigs].map(i => i.description ?? 'Unknown');

  const description = sym.description ?? 'Unknown';

  instance.mountOrder.push(description);

  if (!(description in instance.mountCount)) {
    instance.mountCount[description] = 0;
  }
  instance.mountCount[description] += 1;
};

const unmountConfig = (sym: symbol) => {
  mountedConfigs.delete(sym);
  instance.mountedConfigs = [...mountedConfigs].map(i => i.description ?? 'Unknown');
};

declare module '../../init' {
  export interface CosmosGlobals {
    instances?: (CosmosInstance | LegacyCosmosInstace)[];
    configurationContext?: Context<ConfigurationContextValue>;
  }
}

if (windowIsAvailable) {
  if (!window.cosmos.instances) {
    window.cosmos.instances = [];
  }

  window.cosmos.instances.push(instance);

  if (Array.isArray(window[legacyMetaKey])) {
    window.cosmos.instances.push(...window[legacyMetaKey]);
    delete window[legacyMetaKey];
  }

  if (!window[legacyMetaKey]) {
    window[legacyMetaKey] = {
      push: inst => {
        window.cosmos.instances?.push(inst);
      }
    };
  }
}

// https://styled-components.com/docs/faqs#shouldforwardprop-is-no-longer-provided-by-default
const shouldForwardProp = (propName: string, target: WebTarget) => {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
};

const Configuration = forwardRef(function Configuration(
  {
    children,
    locale,
    direction,
    translations: customTranslations,
    theme,
    themeMode,
    onThemeModeChange,
    disableDefaultFontLoading,
    styleSheets: styleSheetURLs,
    styleSheetTarget,
    portalTarget,
    renderNativeControls,
    testIds,
    id
  }: PropsWithoutRef<ConfigurationProps>,
  ref: ConfigurationProps['ref']
) {
  const context = windowIsAvailable
    ? window.cosmos.configurationContext ?? ConfigurationContext
    : ConfigurationContext;
  const ctx = useContext(context);
  const themeMachine = theme
    ? new ThemeMachine<DefaultThemeDefinition>({ theme, parent: ctx.themeMachine })
    : ctx.themeMachine;
  const translations = useMemo(
    () => createTranslationPack({ ...ctx.translations, ...customTranslations }),
    [ctx.translations, customTranslations]
  );

  const [wrapperEl, setWrapperEl] = useElement<HTMLDivElement>();
  const [connectedWatcherEl, setConnectedWatcherEl] = useElement<PegaConnectedWatcher>();
  // State tracking if the DOM nodes are connected. Only used to trigger rerenders when connected state changes.
  const [, setConnected] = useState<boolean | null>(null);
  const configRootRef = useConsolidatedRef<HTMLDivElement>(setWrapperEl, ref);

  useEffect(() => {
    if (!connectedWatcherEl) return;

    const connectDisconnectListener = () => {
      setConnected(connectedWatcherEl.isConnected);
    };

    connectedWatcherEl.addEventListener('connect-disconnect', connectDisconnectListener);

    return () => {
      connectedWatcherEl.removeEventListener('connect-disconnect', connectDisconnectListener);
    };
  }, [connectedWatcherEl]);

  // Determine valid style sheet and portal target with ShadowDOM handling.
  const [resolvedStyleSheetTarget, resolvedPortalTarget] = useMemo(() => {
    // When rendering on the server, or before the wrapper is in the DOM, just bail.
    if (!documentIsAvailable || !wrapperEl?.isConnected) return [undefined, undefined];

    const rootNode = wrapperEl.getRootNode();

    // When within the ShadowDOM, the default target is the ShadowRoot, otherwise default to head/body element.
    const defaultStyleSheetTarget = rootNode instanceof ShadowRoot ? rootNode : document.head;
    const defaultPortalTarget = rootNode instanceof ShadowRoot ? rootNode : document.body;

    // Currently passed prop or previous context value
    const requestedStyleSheetTarget = styleSheetTarget ?? ctx.styleSheetTarget;
    const requestedPortalTarget = portalTarget ?? ctx.portalTarget;

    // If the "requested" target is within the root node, it is valid, so use that, otherwise fallback to the default.
    return [
      rootNode.contains(requestedStyleSheetTarget ?? null)
        ? requestedStyleSheetTarget
        : defaultStyleSheetTarget,
      rootNode.contains(requestedPortalTarget ?? null) ? requestedPortalTarget : defaultPortalTarget
    ];
  }, [
    wrapperEl,
    wrapperEl?.isConnected,
    styleSheetTarget,
    portalTarget,
    ctx.styleSheetTarget,
    ctx.portalTarget
  ]);

  // Load global fonts and styles if this configuration instance is...
  const shouldInitialize = useMemo(
    () =>
      // the first configuration instance or...
      !ctx.initialized ||
      // is not within the same root node as the previous configuration instance.
      !!(wrapperEl && wrapperEl.getRootNode() !== ctx.styleSheetTarget?.getRootNode()),
    [ctx.initialized, wrapperEl]
  );

  // If we need to reinitialize, clear the mounted StyleSheetManagers.
  const mountedStyleSheetManagers = shouldInitialize ? [] : ctx.mountedStyleSheetManagers;

  // Check if this StyleSheetManager is mounted above this Configuration.
  const stylesheetManagerMounted = useMemo(
    () => !!mountedStyleSheetManagers?.includes(StyleSheetManager),
    [mountedStyleSheetManagers]
  );

  useEffect(() => {
    const sym = Symbol(id);
    mountConfig(sym);

    return () => {
      unmountConfig(sym);
    };
  }, [id]);

  const wrappedChildren = shouldInitialize ? (
    <>
      {!disableDefaultFontLoading &&
        resolvedStyleSheetTarget &&
        createPortal(
          <link
            rel='stylesheet'
            href='https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,400..700;1,400..700&family=Inter:wght@400..700&family=Roboto+Flex:opsz,wdth,wght@8..144,25..151,400..700&display=swap'
            data-cosmos-global-style
          />,
          resolvedStyleSheetTarget
        )}
      <GlobalStyle />
      {children}
    </>
  ) : (
    <>{children}</>
  );

  const styledWrappedChildren = stylesheetManagerMounted ? (
    wrappedChildren
  ) : (
    <StyleSheetManager
      target={
        // Target must be undefined in a JSDOM environment due to Styled Components server mode handling.
        navigatorIsAvailable &&
        resolvedStyleSheetTarget?.isConnected &&
        !navigator.userAgent.includes('jsdom')
          ? resolvedStyleSheetTarget
          : undefined
      }
      shouldForwardProp={shouldForwardProp}
    >
      {wrappedChildren}
    </StyleSheetManager>
  );

  return (
    <div
      data-config-root
      // Need to use inline styles because the StyleSheetManager is configured within this div.
      style={{ display: 'contents' }}
      ref={configRootRef}
    >
      <context.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          locale: locale ?? ctx.locale,
          direction:
            direction ?? (locale !== undefined ? directionOfLocale(locale) : ctx.direction),
          translations,
          themeMachine,
          themeMode: themeMode ?? ctx.themeMode,
          onThemeModeChange: onThemeModeChange ?? ctx.onThemeModeChange,
          styleSheetTarget: resolvedStyleSheetTarget,
          portalTarget: resolvedPortalTarget,
          loadedRef: ctx.loadedRef,
          renderNativeControls: renderNativeControls ?? ctx.renderNativeControls,
          testIds: testIds ?? ctx.testIds,
          mountedStyleSheetManagers: stylesheetManagerMounted
            ? mountedStyleSheetManagers
            : [...(mountedStyleSheetManagers ?? []), StyleSheetManager],
          initialized: true
        }}
      >
        <ThemeProvider theme={themeMachine.theme}>
          {styleSheetURLs &&
            resolvedStyleSheetTarget &&
            createPortal(
              styleSheetURLs.map(url => (
                <link rel='stylesheet' href={url} key={url} data-cosmos-global-style />
              )),
              resolvedStyleSheetTarget
            )}
          {styledWrappedChildren}
        </ThemeProvider>
      </context.Provider>
      <pega-connected-watcher ref={setConnectedWatcherEl} style={{ display: 'none' }} />
    </div>
  );
});

export default Configuration;
